import React, { useEffect, useMemo, useState } from 'react';
import styles from './Profile.module.scss';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { Button, buttonVariants } from '@/components/Button';
import { buttonSizes } from '@/components/Button/Button.constants';
import { Dropdown } from '@/components/Dropdown';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import DefaultAvatar from '@assets/img/svg/avatar.svg';
import { teamActionTypes } from '@/store/Team/Team.actionTypes';
import { memberActionTypes } from '@/store/Member/Member.actionTypes';
import { authActionTypes } from '@/store/Auth/Auth.actionTypes';
import { UnfreezeUI } from '@/assets/plugins/nucleo/js/freeze-ui';
import teamDatabase from '@/services/team';
import authService from '@/services/auth';
import { addNotification, notificationStates } from '@/components/Notification';
import { Input } from '@/components/Input';
import { SvgIcon, SvgIconSrc } from '@components/SvgIcon';
import { useFormChanges } from '@/hooks/useFormChanges';
import { useQuery } from 'react-query';
import { fetchEmployeeExtendedDetails } from '@/services/employeeMemberEnrichment';
import { queryKeys } from '@/config/queryKeys';
import firebase from '@/config/firebase.config';
import { FiCopy } from 'react-icons/fi';

const db = firebase.firestore();
const defaultDropdownStyles = {
  valueContainer: {
    height: '34px',
  },
  control: {
    height: '34px',
  },
};
export const ProfileTab = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [guidCopied, setGuidCopied] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedActiveTeam, setSelectedActiveTeam] = useState(null);
  const [editableName, setEditableName] = useState('');
  const [updatingName, setUpdatingName] = useState(false);
  const [rSelected, setRSelected] = useState(0);

  const userId = useSelector((state) => state.auth?.profile?.id);

  const { data: extendedDetails } = useQuery(
    queryKeys.employeeList.extendedDetails(userId),
    () => fetchEmployeeExtendedDetails(db, userId),
    { enabled: !!userId, staleTime: 5 * 60 * 1000 }
  );

  const handleCopyGuid = () => {
    if (!extendedDetails?.guid || extendedDetails.guid === 'N/A') return;
    navigator.clipboard.writeText(extendedDetails.guid).then(() => {
      setGuidCopied(true);
      setTimeout(() => setGuidCopied(false), 2000);
    });
  };

  const user = useSelector((state) => ({
    userProfile: state.auth?.profile,
    activeCompany: state.auth?.profile?.activeCompany,
    companyList: state.auth?.profile?.companyList,
    activeTeamId: state.team.selectedTeamID,
    activeUserId: state.team.activeUserID,
    all: state.team.all,
    role: state.auth?.profile?.activeCompany?.role,
  }));
  const team = useSelector((state) => ({
    activeTeam: state.team.activeTeam,
    activeUserID: state.team.activeUserID,
    teams: state.team.teams,
    profileID: state.auth.profile.id,
    all: state.team.all,
    selectedTeamID: state.team.selectedTeamID,
    active: state.team.active,
    week: state.team.week,
  }));
  const companyOptions = useMemo(() => {
    return user?.companyList?.map((company) => ({
      ...company,
      value: company?.companyId,
      label: company?.companyName,
    }));
  }, [user?.companyList]);

  const teamOptions = useMemo(() => {
    return team?.teams?.data?.map((team) => {
      return {
        ...team,
        value: team?.activeTeamid,
        label: team?.Team,
      };
    });
  }, [team?.teams?.data]);
  useEffect(() => {
    if (user?.activeCompany) {
      setSelectedCompany({
        ...user?.activeCompany,
        value: user?.activeCompany?.companyId,
        label: user?.activeCompany?.companyName,
      });
    }
  }, [user?.activeCompany]);
  useEffect(() => {
    if (team?.active) {
      setSelectedTeam({
        ...team?.active,
        value: team?.active?.activeTeamid,
        label: team?.active?.Team,
      });
    }
  }, [team?.active]);
  useEffect(() => {
    if (team?.activeTeam) {
      setSelectedActiveTeam({
        ...team?.activeTeam,
        value: team?.activeTeam?.activeTeamId,
        label: team?.activeTeam?.Team,
      });
    }
  }, [team?.activeTeam]);
  useEffect(() => {
    setEditableName(user?.userProfile?.name || '');
  }, [user?.userProfile?.name]);

  const { hasChanges: hasNameChanges } = useFormChanges(
    { name: user?.userProfile?.name },
    { name: editableName },
    ['name']
  );
  const { hasChanges: hasCompanyChanges } = useFormChanges(
    { companyId: user?.activeCompany?.companyId },
    { companyId: selectedCompany?.value },
    ['companyId']
  );
  const { hasChanges: hasTeamChanges } = useFormChanges(
    { teamId: team?.active?.activeTeamid },
    { teamId: selectedTeam?.value },
    ['teamId']
  );
  const { hasChanges: hasActiveTeamChanges } = useFormChanges(
    { activeTeamId: team?.activeTeam?.activeTeamId },
    { activeTeamId: selectedActiveTeam?.value },
    ['activeTeamId']
  );

  const handleChangeTeam = () => {
    if (!selectedTeam?.value || !hasTeamChanges) return;
    setLoading(true);
    let obj = { Team: selectedTeam.label, activeTeamid: selectedTeam.value };
    localStorage.setItem('teamData', JSON.stringify(obj));
    dispatch({
      type: teamActionTypes.SET_SELECT_TEAM_ID,
      payload: {
        selectedTeamID: selectedTeam.value,
        activeUserID: user.userProfile?.id,
        activeTeam: {
          Team: selectedTeam.label,
          activeTeamid: selectedTeam.value,
        },
        companyId: user?.activeCompany?.companyId,
      },
    });
    setTimeLogDays(rSelected, selectedTeam.value);
    UnfreezeUI();
    setLoading(false);
  };
  const setTimeLogDays = (days, id, start, end) => {
    dispatch({
      type: teamActionTypes.SET_TIMELOG_DAY_RANGE,
      payload: { days, start, end },
    });

    dispatch({
      type: memberActionTypes.SET_TIMELOG_DAY_RANGE,
      payload: { days, start, end },
    });

    dispatch({
      type: teamActionTypes.SET_TODAY_TIMES,
      payload: {
        selectedTeamID: id,
        day: days,
      },
    });
    dispatch({
      type: teamActionTypes.SET_TRENDS_DATA,
      payload: {
        selectedTeamID: id,
        week: team.week,
      },
    });

    if (user.all) {
      dispatch({
        type: teamActionTypes.GET_ALL_TEAM_TIMELOG,
        payload: {
          days,
          start,
          end,
          teamId: id,
        },
      });
    } else {
      dispatch({
        type: teamActionTypes.GET_MANAGER_TEAM_COLLECTION,
        payload: {
          days,
          start,
          end,
          activeTeamId: id,
          activeUserId: user.activeUserId,
        },
      });
    }
  };
  const handleChangeCompany = () => {
    if (!selectedCompany?.value || !hasCompanyChanges) return;
    localStorage.setItem('teamData', []);
    dispatch({
      type: teamActionTypes.SWITCH_COMPANY_ACTIVE_TEAM,
      payload: {
        companyId: selectedCompany.value,
        userId: user.userProfile?.id,
      },
    });
  };
  const handleChangeActiveTeam = async () => {
    if (!selectedActiveTeam || !hasActiveTeamChanges) return;
    setLoading(true);
    const teamDocId =
      selectedActiveTeam?.activeTeamid ?? selectedActiveTeam?.teamId;
    const teamLabel =
      selectedActiveTeam?.Team ?? selectedActiveTeam?.teamName ?? '';
    if (!teamDocId) {
      setLoading(false);
      return;
    }
    const cid = user?.activeCompany?.companyId;
    const uid = user?.userProfile?.id;
    if (!cid || !uid) {
      setLoading(false);
      return;
    }

    const updateRes = await teamDatabase.updateMemberActiveCompanyAndTeam(
      uid,
      cid,
      teamDocId
    );
    if (updateRes?.success) {
      addNotification({
        message: `Active team saved: ${teamLabel}`,
        level: notificationStates.success,
      });
      const emailRes = await teamDatabase.sendActiveTeamChangeEmail({
        companyName: user?.activeCompany?.companyName || '',
        teamName: teamLabel,
      });
      if (!emailRes?.success) {
        addNotification({
          message:
            'Active team saved, but the confirmation email could not be sent.',
          level: notificationStates.warning,
        });
      }
    } else {
      addNotification({
        message:
          updateRes?.error?.message ||
          'Could not save active team. Check permissions and try again.',
        level: notificationStates.error,
      });
    }
    setLoading(false);
  };
  const handleUpdateName = async () => {
    const trimmedName = String(editableName ?? '').trim();
    if (!trimmedName) {
      addNotification({
        message: 'Name cannot be empty',
        level: notificationStates.warning,
      });
      return;
    }

    if (trimmedName === String(user?.userProfile?.name ?? '').trim()) {
      addNotification({
        message: 'Name is already up to date',
        level: notificationStates.warning,
      });
      return;
    }

    setUpdatingName(true);
    const response = await authService.updateUserNameEverywhere(trimmedName);

    if (response?.success) {
      // Keep this as the last step after all backend updates.
      dispatch({
        type: authActionTypes.SET_STATE,
        payload: {
          profile: {
            ...user?.userProfile,
            name: trimmedName,
          },
        },
      });
      addNotification({
        message: 'Name updated successfully',
        level: notificationStates.success,
      });
    } else {
      addNotification({
        message: response?.data || 'Failed to update name',
        level: notificationStates.error,
      });
      setEditableName(user?.userProfile?.name || '');
    }

    setUpdatingName(false);
  };
  return (
    <div className={styles.profileTab}>
      <div className={styles.profileTitleContainer}>
        <Text
          variant={textVariantsEnum.Text_xl}
          fontWeight={fontWeightEnum.Bold}
          color="primary-500"
          className={styles.profileTitle}
        >
          Profile
        </Text>
      </div>
      <div className={styles.profileDetailsContainer}>
        <div className={styles.profileDetailsItemContainer}>
          <span className={styles.avatar}>
            <img
              alt="..."
              src={
                !isEmpty(user?.userProfile?.avatar)
                  ? user?.userProfile?.avatar
                  : DefaultAvatar
              }
            />
          </span>
          <div className={styles.profileDetailsItemContent}>
            <Text
              variant={textVariantsEnum.Text_md}
              fontWeight={fontWeightEnum.Medium}
              color="primary-500"
            >
              {user?.userProfile?.name}
            </Text>
            <Text
              variant={textVariantsEnum.Text_md}
              fontWeight={fontWeightEnum.Medium}
              color="primary-500"
            >
              {user?.userProfile?.email}
            </Text>
          </div>
        </div>
      </div>
      <div className={styles.profileActionsContainer}>
        <div className={styles.profileActionsItemContainer}>
          <Input
            label="Name"
            value={editableName}
            onChange={(e) => setEditableName(e?.target?.value || '')}
            style={{
              width: '400px',
            }}
            disabled={updatingName}
          />
          <Button
            text="Update Name"
            disabled={updatingName || !hasNameChanges}
            onClick={handleUpdateName}
            className={'mb-1'}
            style={{
              backgroundColor: '#3363F5',
            }}
          />
        </div>
        <div className={styles.profileActionsItemContainer}>
          <Dropdown
            label={'Active Company'}
            options={companyOptions}
            value={selectedCompany}
            width="400px"
            onChange={(value) => {
              setSelectedCompany(value);
            }}
            selectStyles={{
              ...defaultDropdownStyles,
            }}
          />
          <Button
            text="Change Company"
            onClick={handleChangeCompany}
            disabled={loading || !hasCompanyChanges}
            className={'mb-1'}
            style={{
              backgroundColor: '#3363F5',
            }}
          />
        </div>
        <div className={styles.profileActionsItemContainer}>
          <Dropdown
            label={'Department (View)'}
            tooltip={'Displays the team whose data you are currently viewing.'}
            options={teamOptions}
            value={selectedTeam}
            width="400px"
            onChange={(value) => {
              setSelectedTeam(value);
            }}
            selectStyles={{
              ...defaultDropdownStyles,
            }}
          />
          <Button
            text="Change Department"
            disabled={loading || !hasTeamChanges}
            onClick={handleChangeTeam}
            className={'mb-1'}
            style={{
              backgroundColor: '#3363F5',
            }}
          />
        </div>
        <div className={styles.profileActionsItemContainer}>
          <Dropdown
            label={'Active Department (Tracking)'}
            options={teamOptions}
            tooltip={
              'Defines the team under which your actions, logs, and usage are recorded.'
            }
            value={selectedActiveTeam}
            width="400px"
            onChange={(value) => {
              setSelectedActiveTeam(value);
            }}
            selectStyles={{
              ...defaultDropdownStyles,
            }}
          />
          <Button
            text="Change Active Department"
            disabled={loading || !hasActiveTeamChanges}
            onClick={handleChangeActiveTeam}
            className={'mb-1'}
            style={{
              backgroundColor: '#3363F5',
            }}
          />
        </div>
        <div className={styles.profileActionsItemContainer}>
          <Input
            label="Role"
            value={user?.userProfile?.role}
            disabled
            readOnly
            style={{
              width: '400px',
            }}
          />
        </div>
        {extendedDetails?.activeTeamId && (
          <div className={styles.profileActionsItemContainer}>
            <div style={{ position: 'relative', width: '400px' }}>
              <Input
                label="GUID"
                value={
                  extendedDetails.guid !== 'N/A' ? extendedDetails.guid : ''
                }
                disabled
                readOnly
                style={{ width: '400px', paddingRight: '36px' }}
              />
              <button
                onClick={handleCopyGuid}
                title={guidCopied ? 'Copied!' : 'Copy GUID'}
                style={{
                  position: 'absolute',
                  right: '8px',
                  bottom: '8px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: guidCopied ? '#3363F5' : '#6B7280',
                  padding: '2px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <FiCopy size={15} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
